import type { ISO8601 } from "./index.ts";
import type { Hint, MeetingSummary, TranscriptLine } from "./meeting.ts";

/** Which role a tool is primarily meant for. "both" shows to everyone with no
 * group bias. FSR-vs-CE grouping (via {@link isCERole}) decides sort order only —
 * every user can still see every tool. */
export type ToolAudience = "CE" | "FSR" | "both";

/** Lifecycle of a marketplace tool. Seed + admin-approved tools are "approved" and
 * visible; user suggestions land as "pending" until an admin approves/rejects. */
export type ToolStatus = "approved" | "pending" | "rejected";

/** Where the tool came from — pre-seeded catalog vs a user suggestion. */
export type ToolSource = "seed" | "user";

/** Broad grouping shown as a badge and used for the category dropdown in the
 * "Suggest a tool" form. Free-text is also accepted on suggestions. */
export const MARKETPLACE_CATEGORIES = [
  "Sandbox, Demos & Coding",
  "Capacity, Quota & Sizing",
  "Architecture, Product & Strategy",
  "Internal AI & Workflow",
  "Support & Troubleshooting",
  "Account Intelligence",
  "Pipeline & Forecasting",
  "Pricing, Contracting & Legal",
  "Partnerships & Co-Sell",
  "Customer Evidence",
  "Guides & Enablement",
  "Other",
] as const;

export type MarketplaceCategory = (typeof MARKETPLACE_CATEGORIES)[number];

// ── Developer plugins ──────────────────────────────────────────────────────
/** A marketplace entry is either a plain link ("tool") or an embeddable
 * developer plugin ("plugin") that renders in a sandboxed iframe on Sally. */
export type MarketplaceKind = "tool" | "plugin";

/** Where a plugin renders. */
export type PluginSurface = "live" | "summary";

/** How much meeting context a plugin receives over the bridge. Admin-set. */
export type PluginDataScope = "metadata" | "full";

/** Actions a plugin may request through the parent bridge (admin-approved subset). */
export type PluginAction = "add-note" | "add-hint" | "open-url" | "set-tag";

/** Submission lifecycle for a developer-suggested plugin. Automated security
 * review runs BEFORE an admin ever sees it. `status:"approved"` is only set when
 * `reviewState:"approved"`. */
export type PluginReviewState =
  | "submitted"
  | "security-review"
  | "changes-requested"
  | "awaiting-admin"
  | "approved"
  | "rejected";

export interface SecurityFinding {
  severity: "low" | "medium" | "high";
  title: string;
  detail: string;
}

export interface SecurityReport {
  verdict: "pass" | "fail";
  findings: SecurityFinding[];
  scannedAt: ISO8601;
}

export const PLUGIN_ACTIONS: readonly PluginAction[] = [
  "add-note",
  "add-hint",
  "open-url",
  "set-tag",
] as const;

/** A tool entry in the marketplace (Firestore `marketplaceTools/{id}`). */
export interface MarketplaceTool {
  id: string;
  name: string;
  description: string;
  /** Destination link — typically an internal go/ link. */
  url: string;
  category: string;
  /** A short glyph/emoji representing what the tool does (shown in a colored tile
   * on the card). Optional — the UI falls back to a category icon when absent. */
  icon?: string;
  audience: ToolAudience;
  status: ToolStatus;
  source: ToolSource;
  createdByUid: string;
  createdByName: string;
  createdAt: ISO8601;
  /** Optional: who created the tool (may differ from the submitter). */
  creatorName?: string;
  /** Optional: link to the creator's profile (go/ page, moma, etc.). */
  creatorUrl?: string;
  /** Internal GCS object path of an uploaded image, if any. */
  imagePath?: string;
  /** Content-type of the uploaded image. */
  imageContentType?: string;
  /** True when an image was uploaded — the client shows it via
   * `/api/marketplace/tools/:id/image`. */
  hasImage?: boolean;
  /** Number of distinct users who liked this tool. */
  likeCount: number;
  reviewedByUid?: string;
  reviewedAt?: ISO8601;
  rejectionReason?: string;

  // ── Plugin-only fields (kind === "plugin") ──
  /** "tool" (default, a link) or "plugin" (embeddable widget). */
  kind?: MarketplaceKind;
  /** Developer-hosted widget URL loaded in a sandboxed iframe. */
  embedUrl?: string;
  /** Origin derived from embedUrl (e.g. "https://plugin.example.com"). Added to
   * the CSP frame-src allowlist when approved. */
  origin?: string;
  /** Which screens the plugin renders on. */
  surfaces?: PluginSurface[];
  /** Admin-set data scope handed to the plugin over the bridge. */
  dataScope?: PluginDataScope;
  /** Admin-approved subset of actions the plugin may request. */
  actions?: PluginAction[];
  /** True once approved — gates CSP + rendering. */
  originApproved?: boolean;
  /** Submission pipeline state (security review → admin). */
  reviewState?: PluginReviewState;
  /** Latest automated security-scan result. */
  securityReport?: SecurityReport;
  /** UID of the developer who submitted the plugin (for change-requests). */
  submitterUid?: string;
  /** Who can use this plugin. "private" = only the submitter (auto-approved, no
   * admin needed — it only ever runs in the author's own browser). "public" =
   * everyone, which requires the security scan + admin approval. Missing = public
   * (legacy plugins). */
  visibility?: "private" | "public";
}

/** A tool as returned to the client — adds whether the caller has liked it (and,
 * for plugins, whether the caller has enabled it). */
export interface ToolView extends MarketplaceTool {
  userLiked: boolean;
  userEnabled?: boolean;
}

/** POST /api/marketplace/plugins/suggest (multipart; optional image). Extends the
 * tool suggestion with the plugin-specific fields the developer requests. */
export interface SuggestPluginRequest extends SuggestToolRequest {
  embedUrl: string;
  surfaces: PluginSurface[];
  dataScope: PluginDataScope;
  actions: PluginAction[];
  /** "private" = just for me (no approval); "public" = publish to everyone (needs approval). */
  visibility?: "private" | "public";
}

/** GET /api/marketplace/plugins?surface= — approved plugins the caller may render. */
export interface ListPluginsResponse {
  plugins: ToolView[];
}

/** GET /api/marketplace/plugins/mine — the developer's own submissions + states. */
export interface MySubmissionsResponse {
  plugins: MarketplaceTool[];
}

// ── Plugin ↔ Sally postMessage bridge ──────────────────────────────────────
/** Read-only meeting context handed to a plugin. Transcript/summary are present
 * only when the plugin's dataScope is "full". */
export interface PluginContext {
  surface: PluginSurface;
  dataScope: PluginDataScope;
  meeting: {
    id: string;
    company?: string;
    meetingType?: string;
    role?: string;
  };
  // metadata scope:
  hints?: Hint[];
  followups?: string[];
  // full scope only:
  transcript?: TranscriptLine[];
  summary?: MeetingSummary | null;
}

/** Parent → iframe. */
export type PluginInboundMessage =
  | { type: "sally:init"; nonce: string; capabilities: PluginAction[]; context: PluginContext }
  | { type: "sally:update"; context: PluginContext };

/** iframe → parent. */
export interface PluginActionMessage {
  type: "sally:action";
  nonce: string;
  action: PluginAction;
  payload?: { text?: string; url?: string; tag?: string };
}

/** GET /api/marketplace/tools — approved tools, already sorted (viewer's group
 * first, then most-liked). `isCE` tells the UI which group the viewer belongs to. */
export interface ListToolsResponse {
  tools: ToolView[];
  isCE: boolean;
}

/** POST /api/marketplace/tools/:id/like */
export interface ToggleLikeResponse {
  liked: boolean;
  likeCount: number;
}

/** POST /api/marketplace/tools/suggest (sent as multipart form-data; an optional
 * `image` file part may accompany these fields). */
export interface SuggestToolRequest {
  name: string;
  description: string;
  url: string;
  category: string;
  audience: ToolAudience;
  creatorName?: string;
  creatorUrl?: string;
}

/** GET /api/marketplace/admin/pending */
export interface ListPendingToolsResponse {
  tools: MarketplaceTool[];
}
